import { Link } from "react-router-dom";
import { Trash, Money, PencilSimple, Note } from "@phosphor-icons/react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import RiskBadge from "@/components/RiskBadge";
import StatusBadge from "@/components/StatusBadge";
import { fmtCurrency } from "@/lib/format";

const TIPO_BADGE = {
  cartera_vencida: {
    label: "Cartera vencida",
    cls: "border-destructive/30 bg-destructive/5 text-destructive",
  },
  venta_credito: {
    label: "Venta a crédito",
    cls: "border-chart-3/40 bg-chart-3/10 text-chart-3",
  },
  cuenta_por_pagar: {
    label: "Cuenta por pagar",
    cls: "border-amber-500/40 bg-amber-500/10 text-amber-700",
  },
};

function DebtorRow({ debtor, onDelete, onAbono, onEdit }) {
  const original = Number(debtor.monto_original || debtor.monto_deuda || 0);
  const abonado = Number(debtor.total_abonado || 0);
  const saldo = Number(
    debtor.saldo_pendiente != null && debtor.saldo_pendiente !== ""
      ? debtor.saldo_pendiente
      : original
  );
  const progresoPct = original > 0 ? Math.min(100, (abonado / original) * 100) : 0;
  const vencido = (debtor.dias_vencimiento || 0) > 0 && saldo > 0;
  const saldado = saldo <= 0.01;
  const tipoKey = debtor.tipo_registro || "cartera_vencida";
  const tipoInfo = TIPO_BADGE[tipoKey] || TIPO_BADGE.cartera_vencida;
  const notas = (debtor.notas_comportamiento || "").trim();

  return (
    <TableRow
      data-testid={`debtor-row-${debtor.id}`}
      className="cursor-pointer"
      onClick={() => window.location.assign(`/deudores/${debtor.id}`)}
    >
      <TableCell>
        <div className="font-medium flex items-center gap-2 flex-wrap">
          {debtor.cliente_nombre}
          <span
            data-testid={`tipo-badge-${debtor.id}`}
            className={`text-[10px] px-1.5 py-0.5 rounded-full border uppercase tracking-wider ${tipoInfo.cls}`}
          >
            {tipoInfo.label}
          </span>
          {notas && (
            <span onClick={(e) => e.stopPropagation()}>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid={`notes-btn-${debtor.id}`}
                    className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 transition-colors"
                    title="Ver notas del cliente"
                  >
                    <Note size={12} weight="duotone" /> Notas
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="right"
                  align="start"
                  className="w-72"
                  data-testid={`notes-popover-${debtor.id}`}
                >
                  <div className="space-y-1.5">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                      Notas del cliente
                    </div>
                    <div className="text-sm whitespace-pre-wrap break-words">{notas}</div>
                    {debtor.perfil_cumplimiento && (
                      <div className="text-[11px] text-muted-foreground pt-1 border-t border-border mt-2">
                        Perfil: <span className="font-medium text-foreground">{debtor.perfil_cumplimiento}</span>
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground">{debtor.email}</div>
      </TableCell>
      <TableCell><RiskBadge risk={debtor.risk} /></TableCell>
      <TableCell className="min-w-[180px]">
        <div
          className={`font-mono font-semibold ${vencido ? "text-destructive" : ""}`}
          data-testid={`saldo-${debtor.id}`}
        >
          {fmtCurrency(saldo)}
        </div>
        {abonado > 0 && (
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Abonado {fmtCurrency(abonado)} / {fmtCurrency(original)}
          </div>
        )}
        <Progress value={progresoPct} className="h-1.5 mt-1.5" />
      </TableCell>
      <TableCell>{debtor.dias_vencimiento}d</TableCell>
      <TableCell><StatusBadge status={debtor.status} /></TableCell>
      <TableCell className="text-right">
        <div className="flex gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="outline"
            disabled={saldado}
            onClick={() => onAbono(debtor)}
            data-testid={`abono-btn-${debtor.id}`}
            title={saldado ? "Deuda saldada" : "Registrar abono"}
          >
            <Money size={15} weight="duotone" className="mr-1" /> Abono
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEdit(debtor)}
            data-testid={`edit-debtor-${debtor.id}`}
            title="Editar datos del cliente"
          >
            <PencilSimple size={15} weight="duotone" />
          </Button>
          <Link to={`/deudores/${debtor.id}`}>
            <Button size="sm" variant="outline" data-testid={`open-debtor-${debtor.id}`}>
              Gestionar
            </Button>
          </Link>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => onDelete(debtor.id, e)}
            data-testid={`delete-debtor-${debtor.id}`}
          >
            <Trash size={15} />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function DebtorsTable({ debtors, loading, totalCount, onDelete, onAbono, onEdit }) {
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-md overflow-hidden">
        <div className="p-8 text-center text-muted-foreground">Cargando cartera…</div>
      </div>
    );
  }

  if (debtors.length === 0) {
    const emptyMsg = totalCount === 0
      ? "No tiene deudores registrados. Cargue un CSV o use datos demo."
      : "Ningún deudor coincide con la búsqueda.";
    return (
      <div className="bg-card border border-border rounded-md overflow-hidden">
        <div className="p-12 text-center" data-testid="empty-debtors">
          <div className="overline mb-2">Sin resultados</div>
          <p className="text-muted-foreground">{emptyMsg}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-md overflow-hidden">
      <div className="overflow-x-auto">
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Cliente</TableHead>
              <TableHead>Riesgo</TableHead>
              <TableHead>Saldo pendiente</TableHead>
              <TableHead>Días</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {debtors.map((d) => (
              <DebtorRow key={d.id} debtor={d} onDelete={onDelete} onAbono={onAbono} onEdit={onEdit} />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
